// 客戶 org 選擇器:正規 combobox(text search + 下拉)。
// 聚焦即展開(空字串列出前 N 家已核准 org 可瀏覽)、打字 debounce 查詢、
// 鍵盤 ↑↓ 選 / Enter 確認 / Esc 關、點外關閉、loading/空/無結果狀態、ARIA。
// 只列 state=approved 的 org(PO owner 必須是已核准開發者)。
import { useCallback, useEffect, useRef, useState } from 'react';
import { listOrganizations, type AdminOrgSummary } from '../organizations/organizationsApi';

export interface OrgRef {
  id: string;
  name: string;
  /** 此 org 是否具備「active + production_aid」的 AID 生產授權(配 PO 前提)。 */
  aidGrant: boolean;
}

interface Props {
  value: OrgRef | null;
  onChange: (v: OrgRef | null) => void;
  disabled?: boolean;
}

const LIMIT = 20;
const DEBOUNCE_MS = 300;

/** org 是否有有效的 AID 生產授權(ADR-048:active 且 scope 含 production_aid)。 */
function hasAidGrant(o: AdminOrgSummary): boolean {
  return o.production_grant?.state === 'active' && (o.production_grant.scopes ?? []).includes('production_aid');
}

export default function OrgCombobox({ value, onChange, disabled }: Props) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState(value?.name ?? '');
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState<AdminOrgSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // debounced approved-org 查詢:open 時才查;空字串 → 列出前 N 家可瀏覽。
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    setFailed(false);
    const t = setTimeout(async () => {
      try {
        const page = await listOrganizations({ state: 'approved', query: query.trim() || undefined, limit: LIMIT });
        if (!alive) return;
        setOpts(page.items);
        setActiveIndex(page.items.length ? 0 : -1);
      } catch {
        if (!alive) return;
        setOpts([]);
        setFailed(true);
      } finally {
        if (alive) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [query, open]);

  // 點元件外 → 關閉
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const select = useCallback(
    (o: AdminOrgSummary) => {
      onChange({ id: o.org_id, name: o.name, aidGrant: hasAidGrant(o) });
      setQuery(o.name);
      setOpen(false);
    },
    [onChange],
  );

  const clear = useCallback(() => {
    onChange(null);
    setQuery('');
    setOpts([]);
    setOpen(false);
  }, [onChange]);

  const onInput = (v: string) => {
    setQuery(v);
    setOpen(true);
    // 編輯即讓既有選取失效(未重新挑 = 未選)
    if (value && v !== value.name) onChange(null);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActiveIndex((i) => Math.min(i + 1, opts.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && activeIndex >= 0 && activeIndex < opts.length) {
        e.preventDefault();
        select(opts[activeIndex]);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const listId = 'org-combo-listbox';
  return (
    <div className="adm-combo" ref={boxRef}>
      <div className="adm-combo-input">
        <input
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={open && activeIndex >= 0 ? `org-combo-opt-${activeIndex}` : undefined}
          value={query}
          disabled={disabled}
          placeholder="搜尋公司名…(可直接展開瀏覽)"
          onChange={(e) => onInput(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />
        {value && (
          <button type="button" className="btn-ghost btn-sm adm-combo-clear" onClick={clear} disabled={disabled}>
            清除
          </button>
        )}
      </div>
      {value && <div className="text-tertiary adm-mono adm-combo-selected">已選:{value.id}</div>}
      {open && (
        <ul id={listId} role="listbox" className="adm-combo-list">
          {loading ? (
            <li className="adm-combo-note">查詢中…</li>
          ) : failed ? (
            <li className="adm-combo-note">查詢失敗,稍後再試</li>
          ) : opts.length === 0 ? (
            <li className="adm-combo-note">{query.trim() ? '查無符合的公司' : '沒有已核准的 org'}</li>
          ) : (
            opts.map((o, i) => (
              <li
                key={o.org_id}
                id={`org-combo-opt-${i}`}
                role="option"
                aria-selected={i === activeIndex}
                className={`adm-combo-opt ${i === activeIndex ? 'is-active' : ''}`}
                onMouseEnter={() => setActiveIndex(i)}
                onMouseDown={(e) => {
                  e.preventDefault(); // 搶在 input blur 之前選取
                  select(o);
                }}
              >
                <span className="adm-combo-opt-name">{o.name}</span>
                <span className="adm-combo-opt-meta">
                  <span className="text-tertiary adm-mono">{o.org_id}</span>
                  <span className={`adm-combo-badge ${hasAidGrant(o) ? 'ok' : 'warn'}`}>
                    {hasAidGrant(o) ? 'AID 授權' : '無 AID 授權'}
                  </span>
                </span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
